import { z } from 'zod';

// ---------------------------------------------------------------------------
// Hub operational alerts (Status & Notices) — read surface.
// GET /api/hubs/:hubId/alerts returns the hub's currently-active, tenant-actionable
// connection/credential alerts. Never carries the provider error body (it can embed
// a key fragment) — only provider + status_code + category + counts + timestamps.
// ---------------------------------------------------------------------------

export const hubAlertsParam = z.object({
  hubId: z.string().uuid(),
});
export type HubAlertsParam = z.infer<typeof hubAlertsParam>;

export const hubAlertSeverity = z.enum(['critical', 'warn', 'info']);
export type HubAlertSeverity = z.infer<typeof hubAlertSeverity>;

export const hubAlert = z.object({
  alert_id: z.string(),
  // The hub this alert belongs to — stamped at read time (the per-hub HubDO row
  // doesn't store it). Drives the banner/aggregate deep-link to the hub's Status tab.
  hub_id: z.string(),
  connection_id: z.string().nullable(),
  channel_id: z.string().nullable(),
  // Open string (not an enum): producers define categories as they're wired, and the
  // read surface must not reject a category that ships before this contract updates.
  category: z.string(),
  provider: z.string().nullable(),
  status_code: z.number().nullable(),
  severity: hubAlertSeverity,
  status: z.enum(['active', 'resolved']),
  count: z.number(),
  first_seen_at: z.string(),
  last_seen_at: z.string(),
  last_notified_at: z.string().nullable(),
  resolved_at: z.string().nullable(),
  // Set when this alert is rolled up at the shared org credential (one org_alert shown
  // against every hub linking it) — the fix lives at the org level, not the hub. Null
  // for hub-local alerts.
  org_credential_id: z.string().nullable(),
});
export type HubAlert = z.infer<typeof hubAlert>;

export const hubAlertsListResponse = z.object({
  alerts: z.array(hubAlert),
});
export type HubAlertsListResponse = z.infer<typeof hubAlertsListResponse>;
